import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Play,
  Mic2,
  Radio,
  Headphones,
  Youtube,
  Rss,
  FileText,
  Video,
  Package,
} from "lucide-react";
import { API_URL, FOUNDER_SPEAKING } from "@/lib/constants";

// Placeholder episodes shown until real ones are seeded via POST /api/episodes.
const PLACEHOLDER_EPISODES = [
  {
    id: "p1",
    number: 3,
    title: "What \"Ready\" Actually Means",
    guest_name: "Featured Leader",
    guest_role: "Head of School",
    guest_org: "Independent K-12",
    summary:
      "A quiet conversation about the word every school leader uses and few define — and what changed the year one school stopped assuming and started asking.",
    duration: "38 min",
    cover_image: "https://images.unsplash.com/photo-1519452575417-564c1401ecc0?auto=format&fit=crop&w=1200&q=80",
  },
  {
    id: "p2",
    number: 2,
    title: "Before You Design, Decide",
    guest_name: "Featured Leader",
    guest_role: "Director of College Counseling",
    guest_org: "Independent School",
    summary:
      "How one counseling office reordered its junior-year workflow around a single decision at the start — and gave families a clearer, kinder path from October to May.",
    duration: "34 min",
    cover_image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80",
  },
  {
    id: "p3",
    number: 1,
    title: "The Secret Sauce of Schools That Feel Different",
    guest_name: "Dr. Crystal Gifford",
    guest_role: "Host & CEO",
    guest_org: "EdgeTech Global",
    summary:
      "The pilot. A conversation about the small, quiet decisions that make some schools feel different when you walk in — and what Beyond the Score is here to spotlight this season.",
    duration: "29 min",
    cover_image: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=1200&q=80",
  },
];

const WHO_BELONGS = [
  "Heads of School",
  "Directors",
  "Principals",
  "Curriculum Directors",
  "College Counselors",
  "Academic Deans",
];

const EXPLORES = [
  { roman: "I", title: "What \"ready\" means" },
  { roman: "II", title: "The leader's \"secret sauce\"" },
  { roman: "III", title: "Leading in the age of AI" },
  { roman: "IV", title: "The idea worth stealing" },
];

const YOU_RECEIVE = [
  { icon: FileText, title: "A produced feature article", body: "Written, edited and designed — a shareable asset that lives on your website and ours." },
  { icon: Video, title: "A short branded video", body: "A polished, evergreen clip you can use in admissions, board, and hiring conversations." },
  { icon: Package, title: "Evergreen assets you own", body: "Fully produced by us, at no cost. Yours to use however serves your school best." },
];

const HOW_IT_WORKS = [
  "Tell us about you",
  "Sit for one interview — Zoom or in person",
  "You approve",
  "We publish",
];

export default function BeyondTheScore() {
  const [episodes, setEpisodes] = useState(PLACEHOLDER_EPISODES);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/episodes`)
      .then((r) => {
        if (alive && Array.isArray(r.data) && r.data.length > 0) {
          setEpisodes(r.data);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/newsletter`, { email, source: "beyond-the-score" });
      toast.success("You're on the list. We'll send you every new feature.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const featured = episodes[0];
  const rest = episodes.slice(1);

  return (
    <div data-testid="page-beyond-the-score">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F4C5C] text-[#FDFBF7] grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24 md:pt-36 md:pb-32 grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-7">
            <div className="overline text-[#E3B23C] flex items-center gap-2">
              <Radio size={14} /> The Featured Leader Series
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] mt-4">
              Beyond the <em className="italic text-[#E3B23C]">Score.</em>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#FDFBF7]/90 leading-relaxed max-w-2xl">
              What's working in <strong className="font-semibold">college, career &amp; life readiness</strong> — hosted by Dr. Crystal Gifford, CEO of EdgeTech Global and creator of TestFit®.
            </p>
            <blockquote className="mt-8 pl-6 border-l-2 border-[#E3B23C] font-serif-display text-xl md:text-2xl italic text-[#FDFBF7]/95 max-w-2xl leading-snug">
              "A number could tell us the score. It could never tell us who was ready — for college, career, and life."
            </blockquote>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact?interest=podcast"
                data-testid="bts-cta-guest"
                className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]"
              >
                Be Featured This Season <ArrowUpRight size={16} />
              </Link>
              <a
                href="#featured"
                className="btn-secondary text-[#FDFBF7] border-[#FDFBF7]/60 hover:bg-white/10"
              >
                Watch a Feature
              </a>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-[#FDFBF7]/70">
              <span className="overline text-[#FDFBF7]/50">Also on</span>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-1.5"><Headphones size={16} /> Apple</span>
                <span className="inline-flex items-center gap-1.5"><Mic2 size={16} /> Spotify</span>
                <span className="inline-flex items-center gap-1.5"><Youtube size={16} /> YouTube</span>
                <span className="inline-flex items-center gap-1.5"><Rss size={16} /> RSS</span>
              </div>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <div className="aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-end justify-center">
                <img
                  src={FOUNDER_SPEAKING}
                  alt="Dr. Crystal Gifford, host of Beyond the Score"
                  className="w-full h-full object-contain object-bottom"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#E3B23C] text-[#22223B] px-4 py-3 rounded-md">
                <div className="overline">Your host</div>
                <div className="font-serif-display text-lg">Dr. Crystal Gifford</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION STATEMENT */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="overline">Our mission</div>
          <p className="font-serif-display text-3xl md:text-4xl leading-snug mt-6 text-[#22223B]">
            Spotlight and spread what works, <em className="italic text-[#0F4C5C]">together</em> — building frameworks that empower students to think, choose, and lead.
          </p>
          <p className="mt-6 text-[#22223B]/70">25 years teaching decisions — not just content.</p>
        </div>
      </section>

      {/* FEATURED EPISODE */}
      {featured && (
        <section id="featured" className="pb-24 bg-[#FDFBF7]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="overline">Latest feature</div>
            <div className="mt-6 grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-6">
                <div className="aspect-[4/3] rounded-lg overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="md:col-span-6">
                <div className="text-sm text-[#6B705C] uppercase tracking-widest">
                  Feature {featured.number} · {featured.duration}
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                  {featured.title}
                </h2>
                <p className="mt-6 text-[17px] text-[#22223B]/80 leading-relaxed">
                  {featured.summary}
                </p>
                <div className="mt-6 text-sm">
                  <span className="text-[#0F4C5C] font-medium">{featured.guest_name}</span>
                  <span className="text-[#6B705C]">
                    {featured.guest_role ? ` · ${featured.guest_role}` : ""}
                    {featured.guest_org ? ` · ${featured.guest_org}` : ""}
                  </span>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    data-testid="bts-featured-play"
                    className="btn-primary"
                    onClick={() => toast.info("Player coming soon — join the list to be notified.")}
                  >
                    <Play size={16} strokeWidth={1.6} /> Watch the Feature
                  </button>
                  <Link to="/contact?interest=podcast" className="btn-secondary">
                    Nominate a Leader
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* EPISODE LIBRARY */}
      <section className="pb-24 bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <h2 className="md:col-span-7 font-serif-display text-3xl md:text-4xl leading-tight">
              The library.
            </h2>
            <p className="md:col-span-4 md:col-start-9 text-[#22223B]/70">
              New features every other week. Season one is currently in production.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {rest.map((ep, i) => (
              <article key={ep.id} data-testid={`bts-episode-${i}`} className="card-soft overflow-hidden flex flex-col">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={ep.cover_image} alt={ep.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="text-xs uppercase tracking-widest text-[#6B705C]">
                    Feature {ep.number} · {ep.duration}
                  </div>
                  <h3 className="font-serif-display text-xl mt-3 leading-snug">{ep.title}</h3>
                  <p className="mt-3 text-sm text-[#22223B]/75 leading-relaxed flex-1">{ep.summary}</p>
                  <div className="mt-4 pt-4 border-t border-black/5 text-xs text-[#0F4C5C]">
                    {ep.guest_name}
                    {ep.guest_role ? ` · ${ep.guest_role}` : ""}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHO BELONGS ON THE SHOW */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-6">
              <div className="overline text-[#0F4C5C]">Who belongs on the show</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                Leaders quietly shaping <em className="italic text-[#0F4C5C]">what ready means.</em>
              </h2>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-[#22223B]/75 leading-relaxed">
              We feature the people making the decisions that shape student outcomes — inside independent schools, districts, charter networks and higher education.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {WHO_BELONGS.map((w) => (
              <span key={w} className="px-4 py-2 rounded-full bg-[#FDFBF7] border border-[#0F4C5C]/20 text-[#22223B] text-sm font-medium">
                {w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT EACH FEATURE EXPLORES */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline">What each feature explores</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight max-w-3xl">
            Four questions worth <em className="italic text-[#0F4C5C]">45 minutes of your time.</em>
          </h2>
          <div className="grid md:grid-cols-4 gap-4 mt-14">
            {EXPLORES.map((e) => (
              <div key={e.roman} className="p-8 border border-[#0F4C5C]/10 rounded-lg bg-[#FDFBF7]">
                <div className="font-serif-display italic text-4xl text-[#E3B23C]">{e.roman}</div>
                <h3 className="font-serif-display text-xl mt-4 leading-snug">{e.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YOU RECEIVE / HOW IT WORKS */}
      <section className="section-pad bg-[#0F4C5C] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="overline text-[#E3B23C]">You receive</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
              Evergreen assets you own — <em className="italic text-[#E3B23C]">fully produced, at no cost.</em>
            </h3>
            <div className="mt-8 space-y-4">
              {YOU_RECEIVE.map((y) => (
                <div key={y.title} className="flex gap-4 p-5 rounded-lg border border-white/10 bg-white/[0.03]">
                  <y.icon size={22} strokeWidth={1.5} className="text-[#E3B23C] mt-1 shrink-0" />
                  <div>
                    <div className="font-serif-display text-lg">{y.title}</div>
                    <p className="text-sm text-[#FDFBF7]/75 mt-1">{y.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="overline text-[#E3B23C]">How it works</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
              Simple, respectful, <em className="italic text-[#E3B23C]">and yours to approve.</em>
            </h3>
            <ol className="mt-8 space-y-4">
              {HOW_IT_WORKS.map((step, i) => (
                <li key={step} className="flex gap-4 items-start">
                  <span className="font-serif-display text-3xl text-[#E3B23C] leading-none w-10 shrink-0">0{i + 1}</span>
                  <span className="text-[17px] text-[#FDFBF7]/90 pt-2">{step}</span>
                </li>
              ))}
            </ol>
            <p className="mt-8 text-sm text-[#FDFBF7]/70 leading-relaxed">
              <strong className="text-[#E3B23C]">In return:</strong> introduce us to a peer worth featuring, and keep the conversation open.
            </p>
          </div>
        </div>
      </section>

      {/* APPLY / NEWSLETTER */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6">
            <div className="overline text-[#0F4C5C]">Be featured this season</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-3 leading-tight">
              Apply now.
            </h2>
            <p className="mt-6 text-[17px] text-[#22223B]/80 leading-relaxed max-w-xl">
              If you're an education leader with a "secret sauce" other schools should hear about, tell us about your work and we'll set up a conversation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://testfit.org/BTSapp"
                target="_blank"
                rel="noreferrer"
                data-testid="bts-apply-cta"
                className="btn-primary"
              >
                Apply at testfit.org/BTSapp <ArrowUpRight size={16} />
              </a>
              <Link to="/contact?interest=podcast" className="btn-secondary">
                Or Contact Us Directly
              </Link>
            </div>
            <p className="mt-6 text-sm text-[#6B705C]">
              crystal@crystalgifford.com · (740) 302-3222
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="bts-newsletter-form"
            className="md:col-span-6 card-soft p-8 md:p-10"
          >
            <div className="overline text-[#0F4C5C] flex items-center gap-2">
              <Mic2 size={14} /> Get every feature
            </div>
            <h3 className="font-serif-display text-2xl mt-3 leading-snug">
              Join the leaders getting Beyond the Score in their inbox.
            </h3>
            <p className="mt-3 text-sm text-[#22223B]/70">
              One email per feature. What they said. What worked. What to try this week.
            </p>
            <label className="block mt-6">
              <span className="sr-only">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
                data-testid="bts-newsletter-email"
                className="w-full bg-[#FDFBF7] border border-black/10 rounded-md px-4 py-3 text-[#22223B] placeholder-[#22223B]/40 focus:outline-none focus:ring-1 focus:ring-[#0F4C5C]"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="bts-newsletter-submit"
              className="btn-primary mt-4 w-full justify-center"
            >
              {loading ? "Subscribing..." : "Subscribe"} <ArrowUpRight size={16} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
